/**
 * ComposePreviewCanvas
 *
 * Client-side canvas composition of base image + selected NFT trait layers.
 * Provides instant preview of what the composed NFT will look like.
 */

import React, { useRef, useEffect, useState } from 'react';
import type { ComposeNFT } from '../../api/compose';

interface ComposePreviewCanvasProps {
  baseImageUrl: string;
  selectedNfts: ComposeNFT[];
  launchId?: string;
  baseImageZIndex?: number;
}

export const ComposePreviewCanvas: React.FC<ComposePreviewCanvasProps> = ({
  baseImageUrl,
  selectedNfts,
  baseImageZIndex = 0,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [composing, setComposing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (selectedNfts.length === 0) return;

    let cancelled = false;
    setComposing(true);

    const loadAndDraw = async () => {
      try {
        const SIZE = 512;
        canvas.width = SIZE;
        canvas.height = SIZE;

        // Collect trait layers from selected NFTs with last-selected-wins deduplication.
        // NFTs are passed in selection order, so later entries overwrite earlier ones
        // for the same trait category.
        const layersByCategory = new Map<string, { url: string; z_index: number }>();
        for (const nft of selectedNfts) {
          for (const layer of nft.trait_layers || []) {
            layersByCategory.set(layer.category, { url: layer.url, z_index: layer.z_index });
          }
        }
        const finalLayers = Array.from(layersByCategory.values()).sort((a, b) => a.z_index - b.z_index);

        // Split layers around the base image z-index
        const belowBase = finalLayers.filter(l => l.z_index < baseImageZIndex);
        const aboveBase = finalLayers.filter(l => l.z_index >= baseImageZIndex);

        // Draw layers below base
        for (const layer of belowBase) {
          if (cancelled) return;
          try {
            const img = await loadImage(layer.url);
            if (cancelled) return;
            ctx.drawImage(img, 0, 0, SIZE, SIZE);
          } catch { /* skip */ }
        }

        // Draw base image
        const baseImg = await loadImage(baseImageUrl);
        if (cancelled) return;
        ctx.drawImage(baseImg, 0, 0, SIZE, SIZE);

        // Draw layers above base
        for (const layer of aboveBase) {
          if (cancelled) return;
          try {
            const img = await loadImage(layer.url);
            if (cancelled) return;
            ctx.drawImage(img, 0, 0, SIZE, SIZE);
          } catch { /* skip */ }
        }
      } catch (err) {
        if (!cancelled) console.error('Preview composition failed:', err);
      } finally {
        if (!cancelled) setComposing(false);
      }
    };

    loadAndDraw();

    return () => { cancelled = true; };
  }, [baseImageUrl, selectedNfts, baseImageZIndex]);

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        width={512}
        height={512}
        className="w-full aspect-square rounded-lg bg-neutral-100"
      />
      {composing && (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-900/20 rounded-lg">
          <span className="text-caption font-caption text-white">Composing...</span>
        </div>
      )}
    </div>
  );
};

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // No crossOrigin needed for preview-only display (canvas will be tainted but that's fine)
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
